/* eslint-disable  @typescript-eslint/no-explicit-any */
import { EventEmitter, Injectable, NgZone } from '@angular/core';
import { DataService } from './data.service';
import { DomainService } from './domain.service';
import { PayNowAtTableRequest, PayAtTableNiPayload, PayAtTableGooglePayPayload } from '../models/pay-at-table.model';
import { GooglePayPaymentDetails, GoogleEnv } from '../models/payment.model';
import { ApiService } from './api.service';
import { PatCheckoutConfirmService } from './pat-checkout-confirm.service';

declare global { interface Window { google?: any; } }

@Injectable({ providedIn: 'root' })
export class GooglePayService {
  public GooglePaySuccess = new EventEmitter<boolean>();

  private transactionId: string;
  private readonly allowedCardNetworks = ['VISA', 'MASTERCARD'];
  private readonly allowedAuthMethods = ['CRYPTOGRAM_3DS'];

  // fetched from BE
  private tokenizationSpec: any | null = null;
  private googleEnv: GoogleEnv = 'PRODUCTION';
  private loadingSpec?: Promise<void>;

  constructor(
    private dataService: DataService,
    private apiService: ApiService,
    private domainService: DomainService,
    private patCheckoutConfirmService: PatCheckoutConfirmService,
  ) { }

  public async showGooglePayButton(containerId: string, onClick: () => void): Promise<void> {
    await this.ensureGoogleLoaded();
    await this.ensureTokenizationSpec();

    const client = this.createPaymentsClient(this.googleEnv);
    const ready = await client.isReadyToPay({
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [this.getBaseCardPaymentMethod()],
    }).catch(() => ({ result: false }));

    // The container is behind an *ngIf and callers defer via setTimeout(0),
    // but a slower change-detection cycle can still beat us here — retry
    // briefly instead of silently giving up on the first miss.
    const container = await this.waitForElement(containerId);
    if (!container) return;

    if (!ready?.result) {
      container.innerHTML = '';
      return;
    }

    container.innerHTML = '';

    const button = client.createButton({
      onClick,
      buttonType: 'checkout',
      buttonColor: 'default',
      buttonSizeMode: 'fill',
      buttonRadius: 20,
    });

    container.appendChild(button);
  }

  public getTransactionId(): string | null {
    return this.transactionId;
  }

  /** Availability check (no tokenization needed here) */
  public async isGooglePayAvailable(): Promise<boolean> {
    try {
      await this.ensureGoogleLoaded();
      const client = this.createPaymentsClient(this.googleEnv);
      const isReady = await client.isReadyToPay({
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [this.getBaseCardPaymentMethod()],
      });
      return !!isReady?.result;
    } catch {
      return false;
    }
  }

  /** Open GPay sheet → send token to NI → emit result */
  public async startGooglePaySession(
    orderIds: string[],
    totalPrice: string,
    currencyCode: string,
    payNowRequest: PayNowAtTableRequest | null = null,
    tipAmount: number | 0 = 0,
  ): Promise<void> {
    try {
      await this.ensureTokenizationSpec();   // fetch from BE first
      await this.ensureGoogleLoaded();

      const client = this.createPaymentsClient(this.googleEnv);
      const req = this.buildPaymentDataRequest(totalPrice, currencyCode, this.tokenizationSpec!);

      try { client.prefetchPaymentData(req); } catch { /* noop */ }

      const paymentData = await client.loadPaymentData(req);
      const token = paymentData?.paymentMethodData?.tokenizationData?.token;
      if (!token) throw new Error('No token from Google Pay');
      this.performTransactionNI(paymentData, orderIds, token, payNowRequest, tipAmount, (result) => {
        const success = result === 'SUCCESS';
        this.GooglePaySuccess.emit(success);
      });
    } catch {
      this.GooglePaySuccess.emit(false);
    }
  }

  public async startPayAtTableGooglePaySession(
    payload: PayAtTableNiPayload,
    totalPrice: string,
    currencyCode: string,
    voucherName?: string
  ): Promise<void> {
    try {
      await this.ensureTokenizationSpec();
      await this.ensureGoogleLoaded();

      const client = this.createPaymentsClient(this.googleEnv);
      const req = this.buildPaymentDataRequest(totalPrice, currencyCode, this.tokenizationSpec!);

      try { client.prefetchPaymentData(req); } catch { /* noop */ }

      const paymentData = await client.loadPaymentData(req);
      const token = paymentData?.paymentMethodData?.tokenizationData?.token;
      if (!token) throw new Error('No token from Google Pay');

      this.performPayAtTableTransactionNI(paymentData, payload, token, voucherName, (result) => {
        const success = result === 'SUCCESS';
        this.GooglePaySuccess.emit(success);
      });
    } catch {
      this.GooglePaySuccess.emit(false);
    }
  }

  // ───────────────────────────────────────────────────────────────────
  // Internals
  // ───────────────────────────────────────────────────────────────────

  /** Fetch & cache tokenization spec from backend */
  private ensureTokenizationSpec(): Promise<void> {
    if (this.tokenizationSpec) return Promise.resolve();
    if (this.loadingSpec) return this.loadingSpec;

    const restaurantId = this.dataService.restaurant().id;

    this.loadingSpec = new Promise<void>((resolve) => {
      if (!restaurantId) {
        // safe fallback so UI doesn’t break
        this.tokenizationSpec = {
          type: 'PAYMENT_GATEWAY',
          parameters: { gateway: 'mpgs', gatewayMerchantId: '' }
        };
        this.googleEnv = 'PRODUCTION';
        return resolve();
      }

      this.apiService
        .getGooglePayPaymentDetails(restaurantId)
        .subscribe({
          next: (d: GooglePayPaymentDetails) => {
            this.tokenizationSpec = {
              type: 'PAYMENT_GATEWAY',
              parameters: {
                gateway: d.gateway || 'mpgs',
                gatewayMerchantId: d.merchant_id || '',
                google_merchant_id: d.google_merchant_id,
                google_merchant_name: d.google_merchant_name
              }
            };
            this.googleEnv = d.googleEnv === 'PRODUCTION' ? 'PRODUCTION' : 'TEST';
            resolve();
          },
          error: () => {
            // fallback to TEST
            this.tokenizationSpec = {
              type: 'PAYMENT_GATEWAY',
              parameters: { gateway: 'mpgs', gatewayMerchantId: '' }
            };
            this.googleEnv = 'PRODUCTION';
            resolve();
          }
        });
    });

    return this.loadingSpec;
  }

  private async waitForElement(id: string, attempts = 10, intervalMs = 100): Promise<HTMLElement | null> {
    for (let i = 0; i < attempts; i++) {
      const el = document.getElementById(id);
      if (el) return el;
      await new Promise<void>((resolve) => setTimeout(resolve, intervalMs));
    }
    return document.getElementById(id);
  }

  private googleScriptRetried = false;

  /** Wait for https://pay.google.com/gp/p/js/pay.js */
  private async ensureGoogleLoaded(): Promise<void> {
    const ready = () => !!window.google?.payments?.api?.PaymentsClient;
    if (ready()) return;

    await this.waitForGoogleReady(7000);
    if (ready()) return;

    // The static <script async> tag in index.html can fail to finish in time
    // on a slow/flaky connection with no further retry, silently hiding the
    // button for the rest of the session. Inject a fresh copy once and give
    // it a second window before giving up for good.
    if (!this.googleScriptRetried) {
      this.googleScriptRetried = true;
      this.injectGoogleScript();
      await this.waitForGoogleReady(7000);
    }

    if (!ready()) throw new Error('Google Pay JS not loaded');
  }

  private waitForGoogleReady(timeoutMs: number): Promise<void> {
    const ready = () => !!window.google?.payments?.api?.PaymentsClient;
    return new Promise<void>((resolve) => {
      if (ready()) { resolve(); return; }
      const start = Date.now();
      const t = setInterval(() => {
        if (ready() || Date.now() - start > timeoutMs) {
          clearInterval(t);
          resolve();
        }
      }, 50);
    });
  }

  private injectGoogleScript(): void {
    if (document.querySelector('script[data-gpay-retry]')) return;
    const script = document.createElement('script');
    script.src = 'https://pay.google.com/gp/p/js/pay.js';
    script.async = true;
    script.setAttribute('data-gpay-retry', 'true');
    document.head.appendChild(script);
  }

  private createPaymentsClient(env: GoogleEnv): any {
    return new window.google.payments.api.PaymentsClient({ environment: env || 'PRODUCTION' });
  }

  private getBaseCardPaymentMethod(): any {
    return {
      type: 'CARD',
      parameters: {
        allowedAuthMethods: this.allowedAuthMethods,
        allowedCardNetworks: this.allowedCardNetworks,
        billingAddressRequired: false,
      },
    };
  }

  private buildPaymentDataRequest(totalPrice: string, currencyCode: string, tokenizationSpec: any): any {
    const cardPaymentMethod = {
      ...this.getBaseCardPaymentMethod(),
      tokenizationSpecification: tokenizationSpec,
    };

    return {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [cardPaymentMethod],
      merchantInfo: {
        merchantName: this.tokenizationSpec.parameters.google_merchant_name,
        merchantId: this.tokenizationSpec.parameters.google_merchant_id
      },
      transactionInfo: {
        totalPriceStatus: 'FINAL',
        totalPrice,
        currencyCode,
        countryCode: 'AE',
      },
      emailRequired: false,
      shippingAddressRequired: false,
    };
  }

  /** POST token to NI via your backend */
  private performTransactionNI(
    paymentData: any,
    orderIds: string[],
    token: string,
    payNowRequest: PayNowAtTableRequest | null = null,
    tipAmount: number,
    callback: (outcome: 'SUCCESS' | 'FAIL') => void
  ): void {
    const payload: any = {
      googlepay: true,
      subdomain: this.domainService.getSubdomain(),
      order_ids: orderIds,
      googlepay_data: { token, paymentData },
      tip_amount: tipAmount,
    };

    if (payNowRequest) {
      payload.payment_mode = "custom_amount";
      payload.cheque_id = payNowRequest.cheque_id;
      payload.payment_details = {
        amount: payNowRequest.total_amount,
      };
    }

    this.apiService.getNiSession(payload).subscribe(
      (res: any) => {
        if (res?.result === 'SUCCESS') {
          this.transactionId = res.order?.id?.substr(4);
          callback(res.result);
        } else {
          callback(res.result);
        }
      },
      () => callback('FAIL')
    );
  }

  private performPayAtTableTransactionNI(
    paymentData: any,
    payload: PayAtTableNiPayload,
    token: string,
    voucherName: string | undefined,
    callback: (outcome: 'SUCCESS' | 'FAIL') => void
  ): void {
    const payAtTablePayload: PayAtTableGooglePayPayload = {
      ...payload,
      googlepay: true,
      googlepay_data: { token, paymentData },
    };

    // Persist fees + voucher on the cheque before charging: the NI session
    // prices from the cheque's stored discount, so paying without this
    // confirm charges the undiscounted total. On confirm failure the payment
    // must fail — proceeding would overcharge the card.
    this.patCheckoutConfirmService.confirm(payload, voucherName).subscribe(confirmed => {
      if (!confirmed) {
        callback('FAIL');
        return;
      }

      this.apiService.getNiSession(payAtTablePayload).subscribe(
        (res: any) => {
          if (res?.result === 'SUCCESS') {
            this.transactionId = res.order?.id?.substr(4);
            callback(res.result);
          } else {
            callback(res.result);
          }
        },
        () => callback('FAIL')
      );
    });
  }
}
